/**
 * Web mirror of `packages/shared/src/concept-design.ts` (the bundle does not
 * import @gridiron/shared). Everything from the MIRROR marker down must stay
 * byte-identical — `concept-design.mirror.test.ts` enforces it.
 */

// ---- MIRROR (keep identical in web/src/lib/concept-design.ts) ----

export const CONCEPT_DESIGN_TYPES = ['run', 'pass', 'rpo'] as const;
export type ConceptDesignType = (typeof CONCEPT_DESIGN_TYPES)[number];

export const CONCEPT_DESIGN_LABELS: Readonly<Record<ConceptDesignType, string>> = {
  run: 'Run',
  pass: 'Pass',
  rpo: 'RPO',
};

/** Playbook categories whose options carry a design type. */
export const DESIGN_TYPE_CATEGORIES = ['OFF_CONCEPT'] as const;

/** Play fields that accept combined concepts, and their ordered-parts field. */
export const COMBINABLE_CONCEPT_FIELDS = ['offensive_concept', 'defensive_concept'] as const;
export type CombinableConceptField = (typeof COMBINABLE_CONCEPT_FIELDS)[number];
export const CONCEPT_PARTS_FIELD: Readonly<Record<CombinableConceptField, string>> = {
  offensive_concept: 'offensive_concept_parts',
  defensive_concept: 'defensive_concept_parts',
};

/** Most pieces one combined concept may have. */
export const MAX_CONCEPT_PARTS = 5;

/** Wire field for the derived Designed Type. */
export const DESIGNED_PLAY_TYPE_FIELD = 'designed_play_type';

export function isConceptDesignType(value: unknown): value is ConceptDesignType {
  return typeof value === 'string' && (CONCEPT_DESIGN_TYPES as readonly string[]).includes(value);
}

export function isCombinableConceptField(field: string): field is CombinableConceptField {
  return (COMBINABLE_CONCEPT_FIELDS as readonly string[]).includes(field);
}

/**
 * Designed Type from the pieces' design types, in any order. Untagged pieces
 * (null) are ignored; nothing tagged (or no pieces) gives null.
 */
export function deriveDesignedPlayType(
  types: ReadonlyArray<ConceptDesignType | null | undefined>
): ConceptDesignType | null {
  let run = false;
  let pass = false;
  for (const type of types) {
    if (type === 'rpo') return 'rpo';
    if (type === 'run') run = true;
    if (type === 'pass') pass = true;
  }
  if (run && pass) return 'rpo';
  if (run) return 'run';
  if (pass) return 'pass';
  return null;
}

/**
 * The stored concept text for ordered pieces. One piece keeps the option's
 * value (identical to a pre-combination play); several join their labels.
 */
export function combinedConceptName(
  parts: ReadonlyArray<{ value: string; label: string }>
): string | null {
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0]!.value;
  return parts.map((part) => part.label.trim()).join(' ');
}

/**
 * Play Type prefill for a Designed Type: run/pass map straight across, an RPO
 * (or nothing) leaves Play Type for the coach.
 */
export function playTypeForDesign(designed: ConceptDesignType | null): 'run' | 'pass' | null {
  return designed === 'run' || designed === 'pass' ? designed : null;
}
